import type { operations, paths } from "@airange/generated-contracts/sso-center";
import createOpenApiClient from "openapi-fetch";
import {
  type AdminRuntimeAccessConfig,
  resolveAdminRuntimeAccessConfig,
} from "./admin-runtime-access-config";
import { createCenterOpenApiFetch, getCenterOpenApiClientOrigin } from "./center-api";
import { unwrapOpenApiResult } from "./openapi-result";

const ADMIN_BFF_EMPTY_RESPONSE_ERROR = {
  code: "admin.access.bff_empty_response",
  message: "Admin BFF endpoint returned an empty JSON response.",
};

type AdminBffBootstrapJson =
  operations["bootstrap_api_auth_bff_admin_bootstrap_get"]["responses"][200]["content"]["application/json"];
type AdminBffAuthorizeJson =
  operations["authorize_api_auth_bff_admin_authorize_post"]["responses"][200]["content"]["application/json"];
type AdminBffCallbackJson =
  operations["callback_api_auth_bff_admin_callback_post"]["responses"][200]["content"]["application/json"];
type AdminBffLogoutJson =
  operations["logout_api_auth_bff_admin_logout_post"]["responses"][200]["content"]["application/json"];

function createAdminBffOpenApiClient(config: AdminRuntimeAccessConfig, fetchImpl: typeof fetch) {
  const baseUrl = getCenterOpenApiClientOrigin(new URL(config.adminBaseUrl).origin);
  return createOpenApiClient<paths>({
    baseUrl,
    fetch: createCenterOpenApiFetch(baseUrl, fetchImpl),
  });
}

async function resolveAdminBffClient(fetchImpl: typeof fetch) {
  const accessConfig = await resolveAdminRuntimeAccessConfig(fetchImpl);
  return createAdminBffOpenApiClient(accessConfig, fetchImpl);
}

export async function requestAdminBffBootstrap(fetchImpl: typeof fetch = fetch) {
  const client = await resolveAdminBffClient(fetchImpl);
  return (await unwrapOpenApiResult(client.GET("/api/auth/bff/admin/bootstrap"), {
    emptyResponseError: ADMIN_BFF_EMPTY_RESPONSE_ERROR,
  })) satisfies AdminBffBootstrapJson;
}

export async function requestAdminBffAuthorize(
  returnPath: string,
  prompt: "none" | "login" | undefined,
  fetchImpl: typeof fetch = fetch,
) {
  const client = await resolveAdminBffClient(fetchImpl);
  return (await unwrapOpenApiResult(
    client.POST("/api/auth/bff/admin/authorize", {
      body: {
        ...(prompt ? { prompt } : {}),
        return_path: returnPath,
      },
    }),
    { emptyResponseError: ADMIN_BFF_EMPTY_RESPONSE_ERROR },
  )) satisfies AdminBffAuthorizeJson;
}

export async function requestAdminBffCallback(
  code: string,
  state: string,
  fetchImpl: typeof fetch = fetch,
) {
  const client = await resolveAdminBffClient(fetchImpl);
  return (await unwrapOpenApiResult(
    client.POST("/api/auth/bff/admin/callback", {
      body: { code, state },
    }),
    { emptyResponseError: ADMIN_BFF_EMPTY_RESPONSE_ERROR },
  )) satisfies AdminBffCallbackJson;
}

export async function requestAdminBffLogout(fetchImpl: typeof fetch = fetch) {
  const client = await resolveAdminBffClient(fetchImpl);
  return (await unwrapOpenApiResult(client.POST("/api/auth/bff/admin/logout"), {
    emptyResponseError: ADMIN_BFF_EMPTY_RESPONSE_ERROR,
  })) satisfies AdminBffLogoutJson;
}
